import { useFormik } from "formik";
import React, { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useI18n } from "../../lib/i18n/i18n_provider";
import { range } from "../../lib/js_utils/array_utils";
import { isEmail } from "../../lib/validation/string";
import {
  InviteAttendeeInput,
  Order,
  OrderInvites,
  SendOrderInvitesInput,
  useSendOrderInvites,
} from "../../offering/api";
import { Spacer } from "../../offering/spacer";
import {
  getInvitationAttendeeListLabel,
  getOrderInvitesAttendees,
} from "../../offering/template_offering_confirm_order_base";

import "./send_invites.css";

interface SendInvitesProps {
  existingInvites: OrderInvites[];
  offeringName: string;
  order: Order;
  numberOfInvites: number;
  refetchInvites: () => Promise<void>;
}

export function SendInvites(props: SendInvitesProps) {
  const {
    offeringName,
    order,
    existingInvites,
    numberOfInvites,
    refetchInvites,
  } = props;
  const [open, setOpen] = useState<"view" | "invite" | "none">("none");

  const { fbt } = useI18n();
  const attendees = getOrderInvitesAttendees(existingInvites);
  const numberOfInvitesSent = attendees.length;
  const full = numberOfInvites === numberOfInvitesSent;
  const existingAttendees = getOrderInvitesAttendees(existingInvites);

  return (
    <div className="send-invites-v2 p-3 bg-info">
      {numberOfInvitesSent > 0 ? (
        <span className="d-flex align-items-center">
          {fbt(
            "Invitation sent to " +
              fbt.plural("attendee", numberOfInvitesSent, {
                name: "number of attendees",
                showCount: "yes",
                many: "attendees",
              })
          )}{" "}
          (
          <Button
            variant="link"
            className="p-0"
            onClick={() => setOpen("view")}
          >
            {fbt("view")}
          </Button>
          ).
        </span>
      ) : (
        <span>{fbt("Will there be others joining you?")}</span>
      )}
      {!full && (
        <>
          <Spacer size={8} direction="horizontal" />
          <Button
            onClick={() => setOpen("invite")}
            className="text-uppercase send-invites-v2__invite"
          >
            {fbt("Invite Attendees")}
          </Button>
        </>
      )}
      <Modal
        show={open === "invite"}
        fullscreen="sm-down"
        size="lg"
        onHide={() => setOpen("none")}
        className="header-center"
      >
        <Modal.Header
          closeButton
          className="send-invites-v2-invite-popup__header"
        >
          <Modal.Title className="fw-bold">
            {fbt("Invite Attendees")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {existingAttendees.length > 0 && (
            <p className="text-muted">
              {fbt("Invitations already sent to:")}{" "}
              {getInvitationAttendeeListLabel(existingInvites, fbt)}
            </p>
          )}
          <p>
            {fbt(
              "Fill in the information below to send an email invitation to your attendees with details and directions."
            )}
          </p>
          <InviteAttendees
            existingInvites={existingInvites}
            numberOfInvites={numberOfInvites}
            order={order}
            onComplete={async (response) => {
              if (response) {
                await refetchInvites();
                setOpen("view");
              } else {
                setOpen("none");
              }
            }}
            offeringName={offeringName}
          />
        </Modal.Body>
      </Modal>
      <Modal
        show={open === "view"}
        fullscreen="sm-down"
        onHide={() => setOpen("none")}
        className="header-center"
      >
        <Modal.Header
          closeButton
          className="send-invites-v2-view-popup__header"
        >
          <Modal.Title className="fw-bold">
            {fbt("Invitation sent!")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            {fbt(
              "An email invitation have been sent to your attendees with details and directions."
            )}
          </p>
          {existingInvites.map((invite, index) => {
            return (
              <div key={index}>
                {invite.message && (
                  <div>
                    <h6 className="text-uppercase text-muted">
                      {fbt("Message")}
                    </h6>
                    <p>{invite.message}</p>
                  </div>
                )}
                {invite.attendees.length > 0 && (
                  <div className="mb-3">
                    <h6 className="text-uppercase text-muted">
                      {fbt("Attendees")}
                    </h6>
                    {invite.attendees.map((a, i) => {
                      return (
                        <div
                          key={i}
                          className={`py-1 border-bottom ${
                            i === 0 ? "border-top" : ""
                          }`}
                        >
                          <div>{a.name}</div>
                          <div>
                            {a.email}
                            {a.phone && <span>{` / ${a.phone}`}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          <div className="d-grid gap-2">
            <Button
              variant="primary"
              onClick={() => setOpen("none")}
              className="text-uppercase"
            >
              {fbt("Done")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
}

interface InviteAttendeesProps {
  order: Order;
  offeringName: string;
  numberOfInvites: number;
  existingInvites: OrderInvites[];
  onComplete: (sent: boolean) => void;
}

function InviteAttendees(props: InviteAttendeesProps) {
  const { offeringName, numberOfInvites, existingInvites, order, onComplete } =
    props;
  // const mq = useMediaQuery();
  const sendOrderInvites = useSendOrderInvites();
  const { fbt } = useI18n();
  const existingAttendees = getOrderInvitesAttendees(existingInvites);
  const inviteRange = existingInvites
    ? range(0, numberOfInvites - existingAttendees.length - 1)
    : range(0, numberOfInvites - 1);
  const initialAttendees = inviteRange.map(() => ({
    name: "",
    email: "",
    phone: "",
  }));

  const { values, setFieldValue, errors, submitForm, isSubmitting } =
    useFormik<SendOrderInvitesInput>({
      initialValues: {
        message: "",
        attendees: initialAttendees,
      },
      initialErrors: {
        message: "",
        attendees: initialAttendees,
      },
      validate: (v) => {
        const errors: SendOrderInvitesInput = {
          message: "",
          attendees: initialAttendees,
        };

        let hasError = false;

        if (v.message) {
          const filledNameOrEmail = v.attendees.some((a) => a.name || a.email);

          if (!filledNameOrEmail) {
            errors.message = fbt("Please enter at least one attendee.");
            return errors;
          }
        }

        v.attendees.forEach((a, i) => {
          if (a.name) {
            if (!a.email) {
              hasError = true;
              errors.attendees[i].email = fbt("Please add email");
            } else if (!isEmail(a.email)) {
              hasError = true;
              errors.attendees[i].email = fbt("Please enter valid email");
            }
          } else if (a.email) {
            if (!a.name) {
              hasError = true;
              errors.attendees[i].name = fbt("Please add name");
            }
          }
        });

        if (!hasError) {
          return {};
        }

        return errors;
      },
      onSubmit: async (v) => {
        const hasAttendees = v.attendees.some((a) => a.name && a.email);

        if (!hasAttendees) {
          onComplete(false);
          return;
        }

        await sendOrderInvites(order.id, {
          ...v,
          attendees: v.attendees.filter((a) => a.name),
        });
        onComplete(true);
      },
    });

  return (
    <Form className="invite-attendee-v2">
      <Form.Group className="mb-3" controlId="sendInvitesMessage">
        <Form.Label className="text-uppercase text-muted">
          {fbt("Message")}
        </Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          placeholder={fbt(
            "i.e. Meet me at " +
              fbt.param("offeringName", offeringName) +
              " for our meeting at 0930."
          )}
          value={values.message}
          onChange={(e) => setFieldValue("message", e.target.value)}
        />
      </Form.Group>

      {inviteRange.map((i) => (
        <AttendeeInput
          key={i}
          index={i}
          errors={errors?.attendees?.[i] as any}
          attendee={values.attendees[i]}
          onChangeName={(_i, v) => setFieldValue(`attendees[${i}].name`, v)}
          onChangeEmail={(_i, v) => setFieldValue(`attendees[${i}].email`, v)}
          onChangePhoneNumber={(_i, v) =>
            setFieldValue(`attendees[${i}].phone`, v)
          }
        />
      ))}

      <div className="row">
        <div className="col-12 col-sm-6">
          <Button
            variant="primary"
            disabled={isSubmitting}
            onClick={submitForm}
            className="w-100"
          >
            {fbt("Send Invitations")}
          </Button>
        </div>
      </div>
    </Form>
  );
}

interface AttendeeInputProps {
  index: number;
  errors?: InviteAttendeeInput;
  attendee: InviteAttendeeInput;
  onChangeName: (index: number, name: string) => void;
  onChangeEmail: (index: number, email: string) => void;
  onChangePhoneNumber: (index: number, phone: string) => void;
}

function AttendeeInput(props: AttendeeInputProps) {
  const {
    index,
    errors,
    attendee,
    onChangeEmail,
    onChangeName,
    onChangePhoneNumber,
  } = props;
  const { fbt } = useI18n();

  return (
    <div className="row mb-3">
      <div className="col-12 col-lg-4">
        <Form.Group controlId={`attendeeName_${index}`}>
          <Form.Label className="text-uppercase text-muted">
            {fbt("Name")}
          </Form.Label>
          <Form.Control
            value={attendee.name}
            onChange={(e) => onChangeName(index, e.target.value)}
          />
          {errors?.name && (
            <Form.Text className="text-danger">{errors.name}</Form.Text>
          )}
        </Form.Group>
      </div>
      <div className="col-6 col-lg-4">
        <Form.Group controlId={`attendeeEmail_${index}`}>
          <Form.Label className="text-uppercase text-muted">
            {fbt("Email")}
          </Form.Label>
          <Form.Control
            type="email"
            value={attendee.email}
            onChange={(e) => onChangeEmail(index, e.target.value)}
          />
          {errors?.email && (
            <Form.Text className="text-danger">{errors.email}</Form.Text>
          )}
        </Form.Group>
      </div>
      <div className="col-6 col-lg-4">
        <Form.Group controlId={`attendeePhone_${index}`}>
          <Form.Label className="text-uppercase text-muted">
            {fbt("Phone (optional)")}
          </Form.Label>
          <Form.Control
            type="tel"
            value={attendee.phone}
            onChange={(e) => onChangePhoneNumber(index, e.target.value)}
          />
          {errors?.email && (
            <Form.Text className="text-danger">{errors.phone}</Form.Text>
          )}
        </Form.Group>
      </div>
    </div>
  );
}
